import PageBackTitle from "../Component/PageBackTitle";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import Pagination from "../Component/Pagination";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../src/Config";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { debounce } from "lodash";
import useClickOutside from "../useClickOutside";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import { ThemeContext } from "../../src/ThemeContext";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import "./expense.css";
import { Link } from "react-router-dom";
import { MdOutlineModeEditOutline } from "react-icons/md";

function AllExpenses({ isSidebarExpanded }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalExpense, setTotalExpense] = useState(0);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(""); // "YYYY-MM"
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isExporting, setIsExporting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [users, setUsers] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  const showSnackbar = (message, sev = "success") => {
    setSnackbarMessage(message);
    setSeverity(sev);
    setSnackbarOpen(true);
  };

   const handleButtonClick = (index, event) => {
    setSelectedRowIndex(index === selectedRowIndex ? null : index);
  };

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const res = await axios.get(`${API_URL}/api/get_user`, {
        headers: { Authorization: authKey },
      });
      if (Array.isArray(res.data)) setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Fetch expenses ─────────────────────────────────────────────────────────
  const fetchExpenses = useCallback(
    async (query, pg, perPage, person, month) => {
      try {
        setLoading(true);
        const authKey = localStorage.getItem("authKey");
        const params = { page: pg, per_page: perPage };
        if (query) params.query = query;
        if (person) params.user_id = person;
        if (month) params.report_month = month;

        const response = await axios.get(`${API_URL}/api/expense_reports`, {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
          params,
        });

        if (response.status === 200) {
          const reports = response.data.reports || [];
          setExpenses(reports);
          setTotalItems(response.data.total || 0);
          setTotalExpense(
            reports.reduce(
              (sum, r) => sum + parseFloat(r.total_expense || 0),
              0,
            ),
          );
        }
      } catch (error) {
        console.error("Error fetching expenses:", error.message);
        showSnackbar("Error fetching expenses", "error");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const debouncedFetch = useCallback(
    debounce((query, pg, perPage, person, month) => {
      fetchExpenses(query, pg, perPage, person, month);
    }, 300),
    [fetchExpenses],
  );

  useEffect(() => {
    debouncedFetch(
      searchInput,
      page,
      itemsPerPage,
      selectedPerson,
      selectedMonth,
    );
  }, [searchInput, page, itemsPerPage, selectedPerson, selectedMonth]);

  useClickOutside([containerRef, buttonRef], () => {
    if (isSortPopup) setIsSortPopup(false);
  });

  // ── Sort ───────────────────────────────────────────────────────────────────
  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(order);
    setIsSortPopup(false);
    const sorted = [...expenses].sort((a, b) => {
      const aVal = (a[column] ?? "").toString().toLowerCase();
      const bVal = (b[column] ?? "").toString().toLowerCase();
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
    setExpenses(sorted);
  };

  // ── Refresh ────────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    setSearchInput("");
    setSelectedPerson("");
    setSelectedMonth("");
    setPage(1);
    setItemsPerPage(50);
    fetchExpenses("", 1, 50, "", "");
  };

  // ── Export ALL visible reports (header button) ─────────────────────────────
  const handleExportAll = async () => {
    if (expenses.length === 0) {
      showSnackbar("No reports to export", "warning");
      return;
    }
    try {
      setIsExporting(true);
      const authKey = localStorage.getItem("authKey");
      for (const expense of expenses) {
        const response = await axios.get(
          `${API_URL}/api/expense_report/${expense.id}/export`,
          {
            headers: { Authorization: authKey },
            responseType: "blob",
            params: { company_name: "Keshav Encon" },
          },
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const month = (expense.report_month || "report").replace("-", "_");
        const name = (expense.employee_name || "employee").replace(/ /g, "_");
        link.setAttribute("download", `Expense_${name}_${month}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      showSnackbar("Reports exported successfully");
    } catch (error) {
      showSnackbar("Error exporting reports", "error");
    } finally {
      setIsExporting(false);
    }
  };

  // ── Row export ─────────────────────────────────────────────────────────────
  const handleExportRow = async (reportId, employeeName, reportMonth) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/expense_report/${reportId}/export`,
        {
          headers: { Authorization: authKey },
          responseType: "blob",
          params: { company_name: "Keshav Encon" },
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const month = reportMonth ? reportMonth.replace("-", "_") : "report";
      const name = (employeeName || "employee").replace(/ /g, "_");
      link.setAttribute("download", `Expense_${name}_${month}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar("Report exported successfully");
    } catch (error) {
      showSnackbar("Error exporting report", "error");
    }
  };

  const handleViewClick = (id) => navigate(`/expense-view/${id}`);
  const handleEditClick = (id) => navigate(`/expense-edit/${id}`);
  const closePopups = () => setIsSortPopup(false);

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const formatCurrency = (val) =>
    `₹${parseFloat(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          {/* ── Header ── */}
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Expenses</PageBackTitle>

              <div className="data-list-heading-content-right-side-expense">
                {/* Date filter — month input (same style as add form) */}
                <div className="expense_filter_group">
                  <span className="expense_month_label">Date</span>
                  <input
                    type="month"
                    className="expense_month_input"
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setPage(1);
                    }}
                    placeholder="select ..."
                  />
                </div>

                {/* Person dropdown */}
                <div className="expense_person_select">
                  <FormControl fullWidth size="small">
                    <InputLabel id="person-filter-label">Person</InputLabel>
                    <Select
                      labelId="person-filter-label"
                      value={selectedPerson}
                      onChange={(e) => {
                        setSelectedPerson(e.target.value);
                        setPage(1);
                      }}
                      label="Person"
                      style={{
                        minWidth: 160,
                        height: "38px",
                        fontSize: "12px",
                      }}
                    >
                      <MenuItem value="">All Persons</MenuItem>
                      {users.map((u) => (
                        <MenuItem key={u.ID} value={u.ID}>
                          {u.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Search */}
                <div className="expense_search_wrap">
                  {/* <IoSearchOutline className="expense_search_icon" /> */}
                  <input
                    type="text"
                    placeholder="Search ..."
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      setPage(1);
                    }}
                    className="search-bar-container"
                  />
                </div>

                {/* Export button in header — not row-wise */}
                <Tooltip title="Export filtered reports to Excel" arrow>
                  <button
                    className="new-button"
                    onClick={handleExportAll}
                    disabled={isExporting}
                  >
                    {isExporting ? "Exporting..." : "Export"}
                  </button>
                </Tooltip>

                {/* New expense */}
                <Tooltip title="New Expense" arrow>
                  <button
                    className="new-button"
                    onClick={() => navigate("/expense-form")}
                  >
                    + New
                  </button>
                </Tooltip>

                {/* Refresh */}
                <Tooltip title="Refresh List" arrow>
                  <button
                    className="expense_refresh_btn"
                    onClick={handleRefresh}
                  >
                    <TbRefresh />
                  </button>
                </Tooltip>

                {/* Sort popup */}
                <button
                  className="expense_sort_btn"
                  onClick={() => setIsSortPopup(!isSortPopup)}
                  ref={buttonRef}
                >
                  <PiDotsThreeOutlineVerticalFill />
                </button>

                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-content">
                      <div className="sort-by">
                        SORT BY{" "}
                        {sortOrder === "asc" ? "(Ascending)" : "(Descending)"}
                      </div>
                      <div className="sort-by-button">
                        <button onClick={() => handleSort("employee_name")}>
                          Employee Name{" "}
                          {sortOrder === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                        <br />
                        <button onClick={() => handleSort("report_month")}>
                          Report Month{" "}
                          {sortOrder === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                        <br />
                        <button onClick={() => handleSort("total_expense")}>
                          Total Expense{" "}
                          {sortOrder === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                        <br />
                        <button onClick={() => handleSort("created_at")}>
                          Entry Date{" "}
                          {sortOrder === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Summary Bar ── */}
          <div className="entity-dashboard">
            <div className="entity-dashboard-contant-expenses">
              {/* <div className="entity-dashboard-contant-item-expenses"> */}
                {/* <div className="entity-icon"></div> */}
                {/* <div className="entity-text-left">
                  <div className="entity-text">
                    <div className="entity-text-title">Total Reports</div>
                    <div className="entity-text-title-total">{totalItems}</div>
                  </div>
                </div> */}
              {/* </div> */}
              <div className="entity-text-right-expenses">
                <div className="entity-text">
                  <div className="entity-text-title">Total Expense</div>
                  <div
                    className="entity-text-title-total"
                    style={{ color: "#2980B9", fontWeight: 700 }}
                  >
                    {formatCurrency(totalExpense)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="data-list-body" onClick={closePopups}>
            <div className="data-list-scroll-body-schedule">
              <div className="data-list-tables">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : expenses.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>PERSON NAME</th>
                        <th>REPORT MONTH</th>
                        <th>LOCATION</th>
                        <th className="center-alignment">CLAIM PERIOD</th>
                        <th className="center-alignment">ITEMS</th>
                        <th className="center-alignment">TOTAL EXPENSE</th>
                        <th>ENTRY DATE</th>
                        <th className="center-alignment">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {expenses.map((expense, index) => (
                        <tr key={expense.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <Tooltip title="Click to view details">
                            <td
                              className="active-td"
                              onClick={() => handleViewClick(expense.id)}
                            >
                              {expense.employee_name || "NA"}
                            </td>
                          </Tooltip>
                          <td>
                            {expense.report_month
                              ? (() => {
                                  try {
                                    return new Date(
                                      expense.report_month + "-01",
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      year: "numeric",
                                    });
                                  } catch {
                                    return expense.report_month;
                                  }
                                })()
                              : "NA"}
                          </td>
                          <td>{expense.location || "NA"}</td>
                          <td className="center-alignment">
                            {expense.claim_period_start &&
                            expense.claim_period_end
                              ? `${formatDate(expense.claim_period_start)} - ${formatDate(expense.claim_period_end)}`
                              : "NA"}
                          </td>
                          <td className="center-alignment">
                            {expense.item_count ?? 0}
                          </td>
                          <td
                            className="center-alignment"
                            style={{ fontWeight: 600, color: "#2980B9" }}
                          >
                            {formatCurrency(expense.total_expense)}
                          </td>
                          <td>{formatDate(expense.created_at)}</td>
                          <td className="center-alignment">
                            <div className="expense_action_wrap">
                              <button
                                onClick={() => handleViewClick(expense.id)}
                                className="button-bg"
                              >
                                View
                              </button>
                              {/* <button
                                onClick={() => handleEditClick(expense.id)}
                                className="expense_btn_edit"
                              >
                                Edit
                              </button> */}

                              <button
                                className="edit-data-button"
                                onClick={(event) =>
                                  handleButtonClick(index, event)
                                }
                              >
                                <div className="edit-button">
                                  <MdExpandMore />
                                </div>
                              </button>

                              <Tooltip title="Export to Excel" arrow></Tooltip>
                            </div>
                          </td>
                          {selectedRowIndex === index && (
                            <div
                              className="edit-popup"
                              style={{
                                position: "absolute",
                                top: "60.5%",
                                right: "5%",
                              }}
                              ref={popupRef}
                            >
                              <div className="edit-popup-contant">
                                <div className="edit-popup-button">
                                  <Link
                                    to={`/expense-edit/${expense.id}`}
                                    className="inquiry-new"
                                  >
                                    <button className="edit-button-inquiry">
                                      <MdOutlineModeEditOutline /> Edit
                                    </button>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Expenses Found</div>
                  </div>
                )}
              </div>

              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={(currentPage, perPage) => {
                  setPage(currentPage);
                  setItemsPerPage(perPage);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default AllExpenses;